import type {
  DocumentExportBranding,
} from './documentExport';

export interface DocumentBrandingSettingsSource {
  logoDataUrl?: string | null;
  headerText?: string | null;
  footerText?: string | null;

  showLogo?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  showPageNumbers?: boolean;

  logoWidthMm?: number;

  marginTopMm?: number;
  marginRightMm?: number;
  marginBottomMm?: number;
  marginLeftMm?: number;
}

export interface OfficeDocumentIdentitySource {
  officeName?: string | null;
  legalName?: string | null;

  email?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  website?: string | null;

  addressLine?: string | null;
  addressNumber?: string | null;
  addressComplement?: string | null;
  district?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;

  lawyerName?: string | null;
  lawyerOab?: string | null;
  lawyerOabState?: string | null;

  documentFooter?: string | null;
}

export interface DocumentLogoImage {
  type: 'png' | 'jpg';
  data: Uint8Array;
  width: number;
  height: number;
}

const clean = (
  value:
    string |
    null |
    undefined
) =>
  String(value ?? '')
    .trim();

const joinNonEmpty = (
  values:
    Array<
      string |
      null |
      undefined
    >,
  separator = ' • '
) =>
  values
    .map(clean)
    .filter(Boolean)
    .join(separator);

const buildOfficeAddress = (
  office:
    OfficeDocumentIdentitySource
) => {
  const street =
    joinNonEmpty(
      [
        office.addressLine,
        office.addressNumber,
      ],
      ', '
    );

  const cityState =
    joinNonEmpty(
      [
        office.city,
        office.state,
      ],
      ' / '
    );

  return joinNonEmpty(
    [
      street,
      office.addressComplement,
      office.district,
      cityState,
      office.zipCode,
    ],
    ' - '
  );
};

const buildOab = (
  office:
    OfficeDocumentIdentitySource
) => {
  const number =
    clean(
      office.lawyerOab
    );

  if (!number) {
    return '';
  }

  const state =
    clean(
      office.lawyerOabState
    );

  return state
    ? `OAB/${state} ${number}`
    : `OAB ${number}`;
};

export const buildAutomaticDocumentHeader = (
  office:
    OfficeDocumentIdentitySource
) => {
  const officeName =
    clean(
      office.officeName
    ) ||
    clean(
      office.legalName
    );

  const lawyer =
    joinNonEmpty(
      [
        office.lawyerName,
        buildOab(office),
      ],
      ' — '
    );

  return [
    officeName,
    lawyer,
  ]
    .filter(Boolean)
    .join('\n');
};

export const buildAutomaticDocumentFooter = (
  office:
    OfficeDocumentIdentitySource
) => {
  const storedFooter =
    clean(
      office.documentFooter
    );

  if (storedFooter) {
    return storedFooter;
  }

  const address =
    buildOfficeAddress(
      office
    );

  const contacts =
    joinNonEmpty(
      [
        office.phone,
        office.whatsapp,
        office.email,
        office.website,
      ]
    );

  return [
    address,
    contacts,
  ]
    .filter(Boolean)
    .join('\n');
};

export const buildDocumentExportBranding = ({
  branding,
  office,
}: {
  branding:
    DocumentBrandingSettingsSource;

  office:
    OfficeDocumentIdentitySource |
    null |
    undefined;
}): DocumentExportBranding => {
  const safeOffice =
    office ?? {};

  const customHeader =
    clean(
      branding.headerText
    );

  const customFooter =
    clean(
      branding.footerText
    );

  return {
    logoDataUrl:
      clean(
        branding.logoDataUrl
      ) || null,

    headerText:
      (
        customHeader ||
        buildAutomaticDocumentHeader(
          safeOffice
        )
      ) || null,

    footerText:
      (
        customFooter ||
        buildAutomaticDocumentFooter(
          safeOffice
        )
      ) || null,

    showLogo:
      branding.showLogo ??
      true,

    showHeader:
      branding.showHeader ??
      true,

    showFooter:
      branding.showFooter ??
      true,

    showPageNumbers:
      branding.showPageNumbers ??
      true,

    logoWidthMm:
      Number(
        branding.logoWidthMm ??
        28
      ),

    marginTopMm:
      Number(
        branding.marginTopMm ??
        25
      ),

    marginRightMm:
      Number(
        branding.marginRightMm ??
        20
      ),

    marginBottomMm:
      Number(
        branding.marginBottomMm ??
        20
      ),

    marginLeftMm:
      Number(
        branding.marginLeftMm ??
        30
      ),
  };
};

const decodeBase64 = (
  value: string
) => {
  const binary =
    atob(
      value.replace(
        /\s+/g,
        ''
      )
    );

  const bytes =
    new Uint8Array(
      binary.length
    );

  for (
    let index = 0;
    index < binary.length;
    index += 1
  ) {
    bytes[index] =
      binary.charCodeAt(
        index
      );
  }

  return bytes;
};

const readUint32Be = (
  bytes: Uint8Array,
  offset: number
) =>
  (
    (
      bytes[offset] *
      0x1000000
    ) +
    (
      bytes[offset + 1] <<
      16
    ) +
    (
      bytes[offset + 2] <<
      8
    ) +
    bytes[offset + 3]
  ) >>> 0;

const readPngDimensions = (
  bytes: Uint8Array
) => {
  if (
    bytes.length < 24 ||
    bytes[0] !== 0x89 ||
    bytes[1] !== 0x50 ||
    bytes[2] !== 0x4e ||
    bytes[3] !== 0x47
  ) {
    return null;
  }

  const width =
    readUint32Be(
      bytes,
      16
    );

  const height =
    readUint32Be(
      bytes,
      20
    );

  if (
    width < 1 ||
    height < 1
  ) {
    return null;
  }

  return {
    width,
    height,
  };
};

const JPEG_SOF_MARKERS =
  new Set([
    0xc0,
    0xc1,
    0xc2,
    0xc3,
    0xc5,
    0xc6,
    0xc7,
    0xc9,
    0xca,
    0xcb,
    0xcd,
    0xce,
    0xcf,
  ]);

const readJpegDimensions = (
  bytes: Uint8Array
) => {
  if (
    bytes.length < 4 ||
    bytes[0] !== 0xff ||
    bytes[1] !== 0xd8
  ) {
    return null;
  }

  let offset = 2;

  while (
    offset + 8 <
    bytes.length
  ) {
    if (
      bytes[offset] !==
      0xff
    ) {
      offset += 1;
      continue;
    }

    const marker =
      bytes[
        offset + 1
      ];

    if (
      JPEG_SOF_MARKERS.has(
        marker
      )
    ) {
      const height =
        (
          bytes[
            offset + 5
          ] << 8
        ) |
        bytes[
          offset + 6
        ];

      const width =
        (
          bytes[
            offset + 7
          ] << 8
        ) |
        bytes[
          offset + 8
        ];

      if (
        width > 0 &&
        height > 0
      ) {
        return {
          width,
          height,
        };
      }

      return null;
    }

    if (
      marker === 0xd9 ||
      marker === 0xda
    ) {
      break;
    }

    if (
      marker === 0xd8 ||
      marker === 0x01 ||
      (
        marker >= 0xd0 &&
        marker <= 0xd7
      )
    ) {
      offset += 2;
      continue;
    }

    if (
      offset + 3 >=
      bytes.length
    ) {
      break;
    }

    const length =
      (
        bytes[
          offset + 2
        ] << 8
      ) |
      bytes[
        offset + 3
      ];

    if (length < 2) {
      break;
    }

    offset +=
      2 + length;
  }

  return null;
};

export const decodeDocumentLogo = (
  dataUrl: string
): DocumentLogoImage => {
  const match =
    /^data:image\/(png|jpeg);base64,(.+)$/i.exec(
      dataUrl.trim()
    );

  if (!match) {
    throw new Error(
      'Formato de logomarca inválido.'
    );
  }

  const type =
    match[1].toLowerCase() ===
    'png'
      ? 'png'
      : 'jpg';

  const data =
    decodeBase64(
      match[2]
    );

  const dimensions =
    type === 'png'
      ? readPngDimensions(
          data
        )
      : readJpegDimensions(
          data
        );

  if (!dimensions) {
    throw new Error(
      'Não foi possível identificar as dimensões da logomarca.'
    );
  }

  return {
    type,
    data,
    width:
      dimensions.width,
    height:
      dimensions.height,
  };
};

export const millimetersToTwips = (
  value: number
) =>
  Math.round(
    value *
    1440 /
    25.4
  );

export const millimetersToPoints = (
  value: number
) =>
  value *
  72 /
  25.4;

export const millimetersToPixels = (
  value: number
) =>
  value *
  96 /
  25.4;